import { useEffect, useState } from "react";
import { useUser } from "@clerk/nextjs";
import type {
  UserPlanClientData,
  UserPlanResponse,
} from "@/lib/types/user-plan";

type UserPlanState = {
  data: UserPlanClientData | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  isLoaded: boolean;
};

export const useUserPlan = (): UserPlanState => {
  const { user, isLoaded } = useUser();
  const [data, setData] = useState<UserPlanClientData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isLoaded) return;

    if (!user) {
      setData(null);
      setIsLoading(false);
      return;
    }

    let isActive = true;

    const loadPlan = async () => {
      setIsLoading(true);
      try {
        const response = await fetch("/api/user/plan", {
          method: "GET",
          cache: "no-store",
        });

        const payload = (await response.json()) as UserPlanResponse;

        if (!isActive) return;

        if (!response.ok || !payload.success || !payload.data) {
          setData(null);
          return;
        }

        setData({
          ...payload.data,
          currentPeriodEnd: payload.data.currentPeriodEnd
            ? new Date(payload.data.currentPeriodEnd)
            : null,
        });
      } catch (error) {
        if (isActive) {
          console.error("Failed to load user plan", error);
          setData(null);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadPlan();

    return () => {
      isActive = false;
    };
  }, [isLoaded, user?.id]);

  return {
    data,
    isLoading,
    isLoggedIn: Boolean(user),
    isLoaded,
  };
};
